import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { 
  Network, User, Building2, RefreshCw, Loader2, ZoomIn, ZoomOut,
  RotateCcw, Settings, Eye, EyeOff, Maximize2, Download
} from 'lucide-react';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Farben für verschiedene Knotentypen
const NODE_COLORS = {
  klient: '#3b82f6',       // Blau
  betreuer: '#8b5cf6',     // Lila
  institution: '#f59e0b',  // Amber
  person: '#10b981',       // Grün
  gericht: '#ef4444',      // Rot
  bank: '#06b6d4',         // Cyan
  arzt: '#ec4899',         // Pink
  default: '#6b7280'       // Grau
};

// Knotentyp zu Icon-Mapping
const NODE_ICONS = {
  klient: 'K',
  betreuer: 'B',
  institution: 'I',
  person: 'P',
  gericht: 'G',
  bank: '$',
  arzt: '+',
  default: '?'
};

export default function Netzwerk3DPage() {
  // State
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [linkDistance, setLinkDistance] = useState(100);
  const [nodeSize, setNodeSize] = useState(8);
  const [filterTyp, setFilterTyp] = useState('alle');
  
  const graphRef = useRef();
  const containerRef = useRef();

  // Daten laden
  useEffect(() => {
    loadGraphData();
  }, []);

  const loadGraphData = async () => {
    setLoading(true);
    try {
      // Verwende den optimierten graph-daten Endpoint
      const graphRes = await fetch(`${API}/api/netzwerk/graph-daten?include_orphans=true&max_nodes=500`);
      
      if (graphRes.ok) {
        const graphData = await graphRes.json();
        const nodes = [];
        const links = [];
        const nodeIds = new Set();
        
        // Nodes aus Backend-Daten transformieren
        (graphData.nodes || []).forEach(n => {
          if (!nodeIds.has(n.entity_id)) {
            const typ = n.typ || 'default';
            nodes.push({
              id: n.entity_id,
              name: n.name || 'Unbekannt',
              typ: typ,
              color: NODE_COLORS[typ] || NODE_COLORS.default,
              size: n.size || 8,
              data: n
            });
            nodeIds.add(n.entity_id);
          }
        });
        
        // Links aus Backend-Daten transformieren
        (graphData.links || []).forEach(l => {
          // Backend liefert source/target als "typ_id" Format
          const sourceId = l.source_entity_id || l.source?.replace(/^[^_]+_/, '');
          const targetId = l.target_entity_id || l.target?.replace(/^[^_]+_/, '');
          
          if (sourceId && targetId && nodeIds.has(sourceId) && nodeIds.has(targetId)) {
            links.push({
              source: sourceId,
              target: targetId,
              label: l.label || l.beziehung_art || 'Verbindung',
              color: '#94a3b8',
              data: l
            });
          }
        });
        
        setGraphData({ nodes, links });
        toast.success(`${nodes.length} Knoten, ${links.length} Verbindungen geladen`);
      } else {
        // Fallback: Lade Daten einzeln
        const klientenRes = await fetch(`${API}/api/betreuung/klienten`);
        const klienten = klientenRes.ok ? await klientenRes.json() : [];
        
        const verkRes = await fetch(`${API}/api/netzwerk/verknuepfungen`);
        const verknuepfungen = verkRes.ok ? await verkRes.json() : [];

        const nodes = [];
        const links = [];
        const nodeIds = new Set();

        // Klienten als Knoten
        klienten.forEach(k => {
          if (!nodeIds.has(k.id)) {
            nodes.push({
              id: k.id,
              name: `${k.vorname} ${k.nachname}`,
              typ: 'klient',
              color: NODE_COLORS.klient,
              size: 12,
              data: k
            });
            nodeIds.add(k.id);
          }
        });

        // Verknüpfungen als Links
        verknuepfungen.forEach(v => {
          const sourceId = v.quell_id;
          const targetId = v.ziel_id;
          
          if (nodeIds.has(sourceId) && nodeIds.has(targetId)) {
            links.push({
              source: sourceId,
              target: targetId,
              label: v.beziehung_art || v.typ || 'Verbindung',
              color: '#94a3b8',
              data: v
            });
          }
        });

        setGraphData({ nodes, links });
        toast.success(`${nodes.length} Knoten, ${links.length} Verbindungen geladen`);
      }
    } catch (e) {
      console.error('Laden fehlgeschlagen:', e);
      toast.error('Graph-Daten konnten nicht geladen werden');
    }
    setLoading(false);
  };

  // Node-Klick Handler
  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    
    // Kamera auf Knoten fokussieren
    if (graphRef.current) {
      const distance = 200;
      const distRatio = 1 + distance / Math.hypot(node.x, node.y, node.z);
      
      graphRef.current.cameraPosition(
        { x: node.x * distRatio, y: node.y * distRatio, z: node.z * distRatio },
        node,
        1000
      );
    }
  }, []);

  // Zoom-Funktionen
  const zoomIn = () => {
    if (graphRef.current) {
      const pos = graphRef.current.cameraPosition();
      graphRef.current.cameraPosition({ 
        x: pos.x * 0.8, 
        y: pos.y * 0.8, 
        z: pos.z * 0.8 
      }, null, 500);
    }
  };

  const zoomOut = () => {
    if (graphRef.current) {
      const pos = graphRef.current.cameraPosition();
      graphRef.current.cameraPosition({ 
        x: pos.x * 1.2, 
        y: pos.y * 1.2, 
        z: pos.z * 1.2 
      }, null, 500);
    }
  };

  const resetView = () => {
    if (graphRef.current) {
      graphRef.current.cameraPosition({ x: 0, y: 0, z: 500 }, { x: 0, y: 0, z: 0 }, 1000);
    }
    setSelectedNode(null);
  };

  // Gefilterte Daten
  const filteredData = filterTyp === 'alle' 
    ? graphData 
    : {
        nodes: graphData.nodes.filter(n => n.typ === filterTyp),
        links: graphData.links.filter(l => {
          const sourceNode = graphData.nodes.find(n => n.id === l.source || n.id === l.source.id);
          const targetNode = graphData.nodes.find(n => n.id === l.target || n.id === l.target.id);
          return sourceNode?.typ === filterTyp || targetNode?.typ === filterTyp;
        })
      };

  // Graph Component (dynamisch importiert)
  const [ForceGraph3D, setForceGraph3D] = useState(null);

  useEffect(() => {
    import('react-force-graph-3d').then(module => {
      setForceGraph3D(() => module.default);
    });
  }, []);

  if (loading || !ForceGraph3D) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-500">3D-Netzwerk wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="netzwerk-3d-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Network className="w-6 h-6 text-blue-600" />
            3D-Netzwerk-Visualisierung
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Interaktive 3D-Ansicht aller Beziehungen
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadGraphData}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Steuerung */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Filter:</span>
              <Select value={filterTyp} onValueChange={setFilterTyp}>
                <SelectTrigger className="w-40 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle Typen</SelectItem>
                  <SelectItem value="klient">Nur Klienten</SelectItem>
                  <SelectItem value="institution">Institutionen</SelectItem>
                  <SelectItem value="person">Personen</SelectItem>
                  <SelectItem value="gericht">Gerichte</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Knotengröße */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Größe:</span>
              <Slider
                value={[nodeSize]}
                onValueChange={([v]) => setNodeSize(v)}
                min={4}
                max={20}
                step={1}
                className="w-24"
              />
            </div>
            
            {/* Labels */}
            <Button 
              variant={showLabels ? "default" : "outline"} 
              size="sm"
              onClick={() => setShowLabels(!showLabels)}
            >
              {showLabels ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
              Labels
            </Button>
            
            {/* Zoom */}
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={zoomIn}>
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={zoomOut}>
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={resetView}>
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Statistik */}
            <div className="flex items-center gap-2 ml-auto">
              <Badge variant="outline">{filteredData.nodes.length} Knoten</Badge>
              <Badge variant="outline">{filteredData.links.length} Verbindungen</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Graph + Details */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* 3D Graph */}
        <Card className="lg:col-span-3">
          <CardContent className="p-0 relative" ref={containerRef}>
            <div className="h-[600px] bg-slate-900 rounded-lg overflow-hidden">
              <ForceGraph3D
                ref={graphRef}
                graphData={filteredData}
                nodeLabel={node => showLabels ? node.name : ''}
                nodeColor={node => node.color || NODE_COLORS.default}
                nodeVal={node => nodeSize}
                nodeOpacity={0.9}
                linkColor={link => link.color || '#475569'}
                linkOpacity={0.4}
                linkWidth={1}
                linkDirectionalArrowLength={3}
                linkDirectionalArrowRelPos={1}
                onNodeClick={handleNodeClick}
                backgroundColor="#0f172a"
                showNavInfo={false}
                enableNodeDrag={true}
                enableNavigationControls={true}
                d3AlphaDecay={0.02}
                d3VelocityDecay={0.3}
                warmupTicks={100}
                cooldownTicks={200}
              />
            </div>
            
            {/* Legende */}
            <div className="absolute bottom-4 left-4 bg-slate-800/90 rounded-lg p-3 text-white text-xs">
              <div className="font-medium mb-2">Legende</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {Object.entries(NODE_COLORS).filter(([k]) => k !== 'default').map(([typ, color]) => (
                  <div key={typ} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                    <span className="capitalize">{typ}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Details Panel */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Details</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedNode ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: selectedNode.color }}
                  >
                    {NODE_ICONS[selectedNode.typ] || NODE_ICONS.default}
                  </div>
                  <div>
                    <h3 className="font-medium">{selectedNode.name}</h3>
                    <Badge variant="outline" className="text-xs capitalize">
                      {selectedNode.typ}
                    </Badge>
                  </div>
                </div>
                
                <div className="text-sm space-y-1 pt-2 border-t">
                  {selectedNode.data && Object.entries(selectedNode.data)
                    .filter(([k, v]) => v && !['id', '_id'].includes(k) && typeof v !== 'object')
                    .slice(0, 8)
                    .map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                        <span className="text-slate-700 truncate max-w-[150px]">{String(value)}</span>
                      </div>
                    ))
                  }
                </div>
                
                {/* Verbindungen */}
                <div className="pt-2 border-t">
                  <h4 className="text-sm font-medium text-slate-600 mb-2">Verbindungen</h4>
                  <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
                    {graphData.links
                      .filter(l => 
                        l.source === selectedNode.id || l.source.id === selectedNode.id ||
                        l.target === selectedNode.id || l.target.id === selectedNode.id
                      )
                      .slice(0, 10)
                      .map((l, i) => {
                        const otherId = (l.source === selectedNode.id || l.source.id === selectedNode.id) 
                          ? (l.target.id || l.target) 
                          : (l.source.id || l.source);
                        const otherNode = graphData.nodes.find(n => n.id === otherId);
                        return (
                          <div 
                            key={i} 
                            className="flex items-center gap-1 text-slate-600 cursor-pointer hover:text-blue-600"
                            onClick={() => otherNode && handleNodeClick(otherNode)}
                          >
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: otherNode?.color || '#6b7280' }}
                            />
                            {otherNode?.name || 'Unbekannt'}
                          </div>
                        );
                      })
                    }
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Network className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Klicken Sie auf einen Knoten</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
